import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { APP_VERSION } from 'src/environments/build-info';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private initialized = false;

  get enabled(): boolean {
    return !!environment.gaEnabled && !!environment.gaMeasurementId;
  }

  init(): void {
    if (!this.enabled || this.initialized) return;

    // Set up dataLayer and gtag stub early
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer!.push(arguments); } as any;

    // Inject GA4 script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${environment.gaMeasurementId}`;
    document.head.appendChild(script);

    // Base initialization
    const gtag = window.gtag!; // assert defined after stub assignment above
    gtag('js', new Date());

    // Disable automatic page_view for SPA; we'll send manually on NavigationEnd
    gtag('set', { send_page_view: false });

    // Configure GA4 with privacy-safe defaults
    gtag('config', environment.gaMeasurementId, {
      anonymize_ip: true,
      debug_mode: !environment.production,
      app_version: APP_VERSION,
      build_number: String(environment.buildNumber ?? '')
    });

    this.initialized = true;
  }

  pageView(path: string, title?: string, location?: string) {
    if (!this.initialized) this.init();
    if (!this.enabled || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title || path,
      page_location: location || (typeof window !== 'undefined' ? window.location.href : undefined)
    });
  }

  setUserId(userId: string | null | undefined) {
    if (!this.initialized) this.init();
    if (!this.enabled || !window.gtag) return;
    if (userId) {
      window.gtag('config', environment.gaMeasurementId, { user_id: String(userId) });
    } else {
      // Clear user_id by reconfiguring without it
      window.gtag('config', environment.gaMeasurementId, { user_id: undefined });
    }
  }
}
